// eslint-disable-next-line no-restricted-imports
import { type ComponentProps, type ComponentType, lazy } from "react";

import { BTSuspense } from "commonComponents/btWrappers/BTSuspense/BTSuspense";

/**
 * This helper will accept the component to load and return a component wrapped in a BTSuspense component
 * @param factory The import path for the component you want to load
 * @returns the component, wrapped in a BTSuspense component
 * @example `const Bill = lazyLoadComponent(() => import("entity/bill/Bill/Bill"));`
 */
export function lazyLoadComponent<T extends ComponentType<any>>(
    factory: () => Promise<{ default: T }>,
) {
    const loadComponent = () =>
        new Promise<{ default: T }>((resolve, reject) => {
            factory()
                .then(resolve)
                .catch(() => {
                    // retry after some time
                    void setTimeout(() => {
                        factory().then(resolve, reject);
                    }, 1500);
                });
        });
    const LazyComponent = lazy(loadComponent);
    return (props: ComponentProps<T>) => (
        <BTSuspense>
            <LazyComponent {...props} />
        </BTSuspense>
    );
}
